// @ts-nocheck
import { Box, Collapse } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CaretDown, CaretUp } from '@phosphor-icons/react';
import { planIsSoldOut, planIsPastDeadline } from '../../../utils/booking';
import PaymentAddOnCard from './PaymentAddOnCard';
import PropTypes from 'prop-types';
import {
  getAddOnPricePercent,
  hasPercentagePrice
} from '../../../utils/percentageAddOns';
import './PaymentAddOnsSection.css';

/**
 * PaymentAddOnsSection component for displaying collapsible add-ons section
 *
 * @namespace Components
 *
 * @param {Object} props - Component props
 * @param {Array} props.addons - Available add-ons for the trip
 * @param {Array} props.passengers - List of passengers from booking
 * @param {Array} props.bookingAddOns - Unified array of add-ons with passenger selections (both existing and new)
 * @param {Function} props.onPassengerToggle - Callback when passenger selection changes
 * @param {boolean} props.isStaff - Whether current user is staff
 * @param {boolean} props.bookingPaidInFull - Whether the booking is paid in full
 * @param {...any} props.rest - Additional props to be spread on the parent element
 *
 * @returns {JSX.Element} React element representing the payment add-ons section
 */
const PaymentAddOnsSection = ({
  addons = [],
  passengers = [],
  bookingAddOns = [],
  onPassengerToggle,
  getPassengerAddOnAmount,
  enablePercentageBasedAddons = false,
  isStaff = false,
  bookingPaidInFull = false,
  ...rest
}) => {
  const { t } = useTranslation(['trip', 'common', 'bookings']);
  const [isExpanded, setIsExpanded] = useState(true);

  // Filter out hidden add-ons and handle auto-hide conditions
  // Staff users (logged in) see all add-ons regardless of visibility settings
  const visibleAddOns = addons.filter((addon) => {
    if (
      !enablePercentageBasedAddons &&
      hasPercentagePrice(getAddOnPricePercent(addon))
    ) {
      return false;
    }

    if (planIsPastDeadline(addon)) return false;

    // Staff users bypass all visibility filters
    if (isStaff) return true;

    // Public users (not logged in) respect visibility settings
    if (addon?.ishiddenfrompassengers) return false;

    // Hide if sold out and autohidewhensoldout is enabled
    if (addon?.autohidewhensoldout && planIsSoldOut(addon)) return false;

    return true;
  });

  // Don't render section if no add-ons available
  if (visibleAddOns.length === 0) return null;

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Box className='payment-addons-section' {...rest}>
      {/* Header */}
      <Box className='payment-addons-section__header' onClick={handleToggle}>
        <Box className='payment-addons-section__header-content'>
          <h3 className='payment-addons-section__title'>
            {bookingPaidInFull
              ? t('bookings:addOptionalItems', 'Add Optional Items')
              : t('bookings:selectAddOns', 'Select Add-Ons')}
          </h3>
          {!isExpanded && (
            <p className='payment-addons-section__subtitle'>
              {visibleAddOns.length}{' '}
              {visibleAddOns.length === 1
                ? t('common:item', 'item')
                : t('common:items', 'items')}{' '}
              {t('common:available', 'available')}
            </p>
          )}
        </Box>
        <Box className='payment-addons-section__chevron'>
          {isExpanded ? <CaretUp size={20} /> : <CaretDown size={20} />}
        </Box>
      </Box>

      {/* Collapsible Content */}
      <Collapse in={isExpanded}>
        <Box className='payment-addons-section__content'>
          {visibleAddOns.map((addon) => {
            // Find this add-on in the bookingAddOns array
            const bookingAddOn = bookingAddOns.find(
              (a) => a.tripaddonid === addon.tripaddonid
            );

            // Get NEWLY selected passenger IDs (bookingpassengeraddonid is null)
            const selectedPassengerIds = bookingAddOn
              ? bookingAddOn.passengers
                  .filter((p) => p.bookingpassengeraddonid === null)
                  .map((p) => p.bookingpassengerid)
              : [];

            // Get existing passenger IDs (those with bookingpassengeraddonid)
            const existingPassengerIds = bookingAddOn
              ? bookingAddOn.passengers
                  .filter((p) => p.bookingpassengeraddonid !== null)
                  .map((p) => p.bookingpassengerid)
              : [];
            const getCardPassengerAddOnAmount = (cardAddOn, passenger) => {
              const passengerAddOn = bookingAddOn?.passengers?.find(
                (p) =>
                  Number(p?.bookingpassengerid) ===
                  Number(passenger?.bookingpassengerid)
              );

              return getPassengerAddOnAmount(cardAddOn, {
                ...passenger,
                pricePerPerson: passengerAddOn?.pricePerPerson
              });
            };

            return (
              <PaymentAddOnCard
                key={addon.tripaddonid}
                addon={addon}
                passengers={passengers}
                selectedPassengerIds={selectedPassengerIds}
                existingPassengerIds={existingPassengerIds}
                onPassengerToggle={onPassengerToggle}
                getPassengerAddOnAmount={getCardPassengerAddOnAmount}
                enablePercentageBasedAddons={enablePercentageBasedAddons}
                isStaff={isStaff}
              />
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
};

PaymentAddOnsSection.propTypes = {
  addons: PropTypes.array,
  passengers: PropTypes.array,
  bookingAddOns: PropTypes.array,
  onPassengerToggle: PropTypes.func.isRequired,
  getPassengerAddOnAmount: PropTypes.func.isRequired,
  enablePercentageBasedAddons: PropTypes.bool,
  isStaff: PropTypes.bool,
  bookingPaidInFull: PropTypes.bool
};

export default PaymentAddOnsSection;
